import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('proxy-option', true);

/**
 * ProxyOption Custom Element
 * Creates a hidden input that mirrors the value of an external option selector
 *
 * Usage: <proxy-option selector="input[name='properties[_buckle]']" input-name="properties[_buckle]"></proxy-option>
 *
 * Each component handles ONE target. Use multiple components for multiple targets.
 * Static listeners ensure only one global change listener regardless of instance count.
 */
class ProxyOption extends HTMLElement {
    static instances = new Map();
    static isListeningForChanges = false;
    static isListeningForReloads = false;

    constructor() {
        super();
        this.proxyInput = null;
    }

    static registerInstance(selector, instance) {
        if (!this.instances.has(selector)) {
            this.instances.set(selector, []);
        }
        this.instances.get(selector).push(instance);

        // Register global listeners only once
        if (!this.isListeningForChanges) {
            document.addEventListener('change', this.handleGlobalChange.bind(this));
            this.isListeningForChanges = true;
        }

        if (!this.isListeningForReloads) {
            document.addEventListener('reload-on-event:loaded', this.handleReloadEvent.bind(this));
            this.isListeningForReloads = true;
        }
    }

    static unregisterInstance(selector, instance) {
        const instances = this.instances.get(selector);
        if (instances) {
            const index = instances.indexOf(instance);
            if (index > -1) {
                instances.splice(index, 1);
            }
            if (instances.length === 0) {
                this.instances.delete(selector);
            }
        }
    }

    static handleGlobalChange(event) {
        // Check if the changed element matches any registered selector
        this.instances.forEach((instances, selector) => {
            if (event.target.matches(selector)) {
                debug.log('Global change detected', { selector, target: event.target });
                instances.forEach((instance) => {
                    instance.syncValue(event.target);
                });
            }
        });
    }

    static handleReloadEvent() {
        debug.log('Reload event detected');
        // Re-sync all instances after a reload
        this.instances.forEach((instances) => {
            instances.forEach((instance) => {
                instance.updateProxyValue();
            });
        });
    }

    connectedCallback() {
        this.selector = this.getAttribute('selector');
        this.inputName = this.getAttribute('input-name');
        this.dataAttribute = this.getAttribute('data-attribute'); // Optional: sync from data-attribute instead of value

        debug.log('Connected', { 
            selector: this.selector, 
            inputName: this.inputName, 
            dataAttribute: this.dataAttribute 
        });

        if (!this.selector || !this.inputName) {
            debug.error('Both "selector" and "input-name" attributes are required', this);
            return;
        }

        this.createProxyInput();
        this.updateProxyValue(); // Initial sync

        ProxyOption.registerInstance(this.selector, this);
    }

    disconnectedCallback() {
        ProxyOption.unregisterInstance(this.selector, this);
    }

    createProxyInput() {
        this.proxyInput = document.createElement('input');
        this.proxyInput.type = 'hidden';
        this.proxyInput.name = this.inputName;
        this.proxyInput.value = '';
        this.appendChild(this.proxyInput);
    }

    syncValue(targetInput) {
        if (!this.proxyInput) return;

        const isCheckable = targetInput.type === 'radio' || targetInput.type === 'checkbox';
        
        // If it's a checkable input, only sync if it's checked
        // If it's not checkable (text, select, etc), always sync
        if (!isCheckable || targetInput.checked) {
            let newValue;
            if (this.dataAttribute) {
                // Sync from data attribute
                newValue = targetInput.getAttribute(`data-${this.dataAttribute}`) || '';
            } else {
                // Sync from value (default behavior)
                newValue = targetInput.value;
            }
            
            if (this.proxyInput.value !== newValue) {
                debug.log('Syncing value', { 
                    from: targetInput, 
                    oldValue: this.proxyInput.value, 
                    newValue,
                    type: targetInput.type
                });
                this.proxyInput.value = newValue;
            }
        }
    }

    updateProxyValue() {
        if (!this.proxyInput) return;

        // Find the target element
        const targetElement = document.querySelector(this.selector);
        
        if (!targetElement) {
            debug.warn('No element found matching selector', { selector: this.selector });
            return;
        }

        let newValue = '';
        const isCheckable = targetElement.type === 'radio' || targetElement.type === 'checkbox';

        if (isCheckable) {
            // For radio/checkbox, find the checked one
            const checkedInput = document.querySelector(`${this.selector}:checked`);
            if (checkedInput) {
                if (this.dataAttribute) {
                    newValue = checkedInput.getAttribute(`data-${this.dataAttribute}`) || '';
                } else {
                    newValue = checkedInput.value;
                }
            } else {
                debug.log('Checkable element found but nothing checked', { selector: this.selector });
            }
        } else {
            // For text/select/etc, use the value directly
            if (this.dataAttribute) {
                newValue = targetElement.getAttribute(`data-${this.dataAttribute}`) || '';
            } else {
                newValue = targetElement.value;
            }
        }
        
        if (this.proxyInput.value !== newValue) {
            debug.log('Updating proxy value', { 
                targetElement, 
                oldValue: this.proxyInput.value, 
                newValue,
                type: targetElement.type
            });
            this.proxyInput.value = newValue;
        }
    }
}

customElements.define('proxy-option', ProxyOption);

export default ProxyOption;
